/**
 * The comparison grid's field registry, and the pure functions that turn it
 * into rows and — once the reviewer has ticked — into a BrandEditor seed.
 *
 * Adding a field to the grid is one entry in {@link PROPOSAL_FIELD_SPECS}; the
 * grid, the tick defaults and the seed builder all iterate the registry and
 * need no change.
 */
import type { MaBrandDetail, MaBrandRow } from '../../services/microbrandAtlas.types';
import type { WatchBrand } from '../../types';
import { normaliseEcommercePlatform } from '../CuratedWatches/BrandCommerceFields';
import type {
  BrandExternalRefs,
  BrandFieldSources,
  CreateBrandSeed,
} from '../CuratedWatches/brandSeeds';

/** The `source` recorded in `fieldSources`, and matched on by nothing else. */
export const MA_SOURCE = 'microbrand-atlas';
export const MA_SOURCE_LABEL = 'Microbrand Atlas';

/**
 * Everything MA told us about one brand: the list row with the detail row laid
 * over it. The list is the only source of `hero_image_url`; the detail is the
 * only source of the commerce and founder columns.
 */
export type MaSource = MaBrandRow & Partial<MaBrandDetail>;

/** Lay a detail row over a list row, never letting a null blank a value. */
export function mergeMaSource(row: MaBrandRow, detail: MaBrandDetail | null | undefined): MaSource {
  const merged: Record<string, unknown> = { ...row };
  if (detail) {
    for (const [key, value] of Object.entries(detail)) {
      if (value !== null && value !== undefined) merged[key] = value;
    }
  }
  return merged as MaSource;
}

export type FieldGroup = 'identity' | 'location' | 'platform' | 'people' | 'media' | 'flags';

export const FIELD_GROUPS: { id: FieldGroup; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'location', label: 'Location' },
  { id: 'platform', label: 'Platform / commerce' },
  { id: 'people', label: 'People' },
  { id: 'media', label: 'Media' },
  { id: 'flags', label: 'Flags' },
];

export type FieldKind = 'text' | 'url' | 'number' | 'boolean' | 'image' | 'location';

export interface ProposalFieldSpec {
  /**
   * The WatchBrand field path this row writes, and the `fieldSources` key it
   * stamps (`country`, `founder.name`). `null` makes the row display-only:
   * shown for the reviewer's reference, never ticked, never written — used
   * where MA has a column WK has no field for.
   */
  wkPath: string | null;
  /** The MA column(s) the proposed value comes from; shown as the row's hint. */
  maPath: string;
  label: string;
  group: FieldGroup;
  kind: FieldKind;
  /** The proposed value in WK's shape. Defaults to reading `maPath` directly. */
  maValue?: (ma: MaSource) => unknown;
  /** What `fieldSources.rawValue` records. Defaults to the proposed value. */
  maRawValue?: (ma: MaSource) => unknown;
  /** WK's current value. Defaults to reading `wkPath` directly. */
  wkValue?: (wk: WatchBrand) => unknown;
  /** Overrides the by-kind equality, e.g. to compare a logo by its source URL. */
  equals?: (wk: WatchBrand, maValue: unknown) => boolean;
  formatter?: (value: unknown) => string;
  /** WK had no such field before this import: an empty WK cell says so. */
  isNewField?: boolean;
  /** Shown on the row whatever its tick state. */
  warning?: string;
  /** The reviewer may rewrite the proposed value before ticking it. */
  editable?: boolean;
  /**
   * Treat a WK `false` as "not set". Only for flags the edit form writes on
   * every save, where `false` is the form's default rather than a decision.
   */
  wkFalseIsEmpty?: boolean;
  /** Overrides the default tick for a row state. */
  defaultChecked?: (state: RowState) => boolean;
  /**
   * Returns why this row is locked for a given WK brand, or null when it is
   * not. A locked row is shown, cannot be ticked, and is never written — the
   * seed builder skips it whatever the tick state says.
   */
  lockedReason?: (wk: WatchBrand) => string | null;
}

/** Stable identity for a spec: its WK path, or its MA path when display-only. */
export function specKey(spec: ProposalFieldSpec): string {
  return spec.wkPath ?? `display:${spec.maPath}`;
}

export const COUNTRY_VERIFIED_LOCK =
  'Country verified in WK — unlock in brand editor to accept MA value.';

export const COUNTRY_WARNING =
  "MA's country is often the registered-entity or manufacturing jurisdiction, not where the brand is from. Check it against the brand's own site.";

function getPath(obj: unknown, path: string): unknown {
  let current: unknown = obj;
  for (const part of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export const PROPOSAL_FIELD_SPECS: ProposalFieldSpec[] = [
  // ── Identity ──────────────────────────────────────────────────────────────
  { wkPath: 'name', maPath: 'name', label: 'Name', group: 'identity', kind: 'text' },
  {
    // WK has no slug field — the document id is the slug, derived from the
    // name at create time and immutable after. Shown for comparison only.
    wkPath: null,
    maPath: 'slug',
    label: 'Slug',
    group: 'identity',
    kind: 'text',
    wkValue: (wk) => wk.id,
  },
  { wkPath: 'displayName', maPath: 'name', label: 'Display name', group: 'identity', kind: 'text' },
  { wkPath: 'founded', maPath: 'founded_year', label: 'Founded', group: 'identity', kind: 'number' },

  // ── Location ──────────────────────────────────────────────────────────────
  {
    wkPath: 'country',
    maPath: 'country',
    label: 'Country',
    group: 'location',
    kind: 'text',
    warning: COUNTRY_WARNING,
    lockedReason: (wk) => (wk.countryVerified === true ? COUNTRY_VERIFIED_LOCK : null),
  },
  { wkPath: 'city', maPath: 'city', label: 'City', group: 'location', kind: 'text', isNewField: true },
  {
    wkPath: 'stateRegion',
    maPath: 'state',
    label: 'State / region',
    group: 'location',
    kind: 'text',
    isNewField: true,
  },
  { wkPath: 'mainAddress', maPath: 'address', label: 'Address', group: 'location', kind: 'text' },
  {
    wkPath: 'location',
    maPath: 'latitude, longitude',
    label: 'Location (lat, lng)',
    group: 'location',
    kind: 'location',
    isNewField: true,
    maValue: (ma) =>
      typeof ma.latitude === 'number' && typeof ma.longitude === 'number'
        ? { lat: ma.latitude, lng: ma.longitude }
        : undefined,
  },
  {
    wkPath: 'assemblyLocation',
    maPath: 'assembly_location',
    label: 'Assembly location',
    group: 'location',
    kind: 'text',
    isNewField: true,
  },
  {
    wkPath: 'designLocation',
    maPath: 'design_location',
    label: 'Design location',
    group: 'location',
    kind: 'text',
    isNewField: true,
  },

  // ── Platform / commerce ───────────────────────────────────────────────────
  { wkPath: 'mainWebsite', maPath: 'website_url', label: 'Website', group: 'platform', kind: 'url' },
  {
    wkPath: 'instagramUrl',
    maPath: 'instagram_url',
    label: 'Instagram',
    group: 'platform',
    kind: 'url',
    isNewField: true,
  },
  {
    // NOT `shopifyEnabled`: that switches on WK's own Shopify product import.
    wkPath: 'isShopifyBrand',
    maPath: 'is_shopify',
    label: 'Shopify storefront',
    group: 'platform',
    kind: 'boolean',
    isNewField: true,
  },
  {
    wkPath: 'ecommercePlatform',
    maPath: 'ecommerce_platform',
    label: 'Ecommerce platform',
    group: 'platform',
    kind: 'text',
    isNewField: true,
    // MA's "normalised" column still mixes "Shopify" with "shopify", and
    // consumers compare the stored value exactly — so it is folded here.
    // `rawValue` in the provenance stamp keeps what MA actually sent.
    maValue: (ma) => normaliseEcommercePlatform(ma.ecommerce_platform) || undefined,
    maRawValue: (ma) => ma.ecommerce_platform,
  },
  {
    wkPath: null,
    maPath: 'product_data_tier',
    label: 'Product data tier',
    group: 'platform',
    kind: 'text',
  },

  // ── People ────────────────────────────────────────────────────────────────
  {
    // Co-founders arrive packed into this one string. It is shown raw and is
    // editable; nothing splits it.
    wkPath: 'founder.name',
    maPath: 'founder_name',
    label: 'Founder name',
    group: 'people',
    kind: 'text',
    isNewField: true,
    editable: true,
  },
  {
    wkPath: 'founder.managed',
    maPath: 'founder_managed',
    label: 'Founder-managed',
    group: 'people',
    kind: 'boolean',
    isNewField: true,
  },
  {
    wkPath: 'founder.lastEditAt',
    maPath: 'last_founder_edit',
    label: 'Last founder edit',
    group: 'people',
    kind: 'text',
    isNewField: true,
  },

  // ── Media ─────────────────────────────────────────────────────────────────
  {
    wkPath: 'logo',
    maPath: 'logo_url',
    label: 'Logo',
    group: 'media',
    kind: 'image',
    // Saving pulls a logo into our Storage, so `logo` never equals MA's URL;
    // the source URL is what says "this is the same image".
    equals: (wk, ma) => wk.logoSourceUrl === ma || wk.logo === ma,
  },
  {
    wkPath: 'heroImage',
    maPath: 'hero_image_url',
    label: 'Hero image',
    group: 'media',
    kind: 'image',
    equals: (wk, ma) => wk.heroImageSourceUrl === ma || wk.heroImage === ma,
    // MA's "hero" is a reference shot of the brand, not a hero image in WK's
    // sense — so it starts unticked whatever its state; the reviewer opts in.
    // (The logo row above keeps the normal default.)
    defaultChecked: () => false,
  },

  // ── Flags ─────────────────────────────────────────────────────────────────
  {
    wkPath: 'isMicroBrand',
    maPath: '(listed in Microbrand Atlas)',
    label: 'Microbrand',
    group: 'flags',
    kind: 'boolean',
    maValue: () => true,
    // MA has no such column: the flag is implied by the brand being listed.
    maRawValue: () => null,
    wkFalseIsEmpty: true,
  },
];

/**
 * - `equal`   both sides hold the same value
 * - `differ`  both sides hold a value and they disagree
 * - `fill`    WK is empty, MA has a value
 * - `new`     as `fill`, on a field WK did not have before this import
 * - `display` reference only; nothing is written
 */
export type RowState = 'equal' | 'differ' | 'fill' | 'new' | 'display';

export interface GridRow {
  key: string;
  spec: ProposalFieldSpec;
  state: RowState;
  wkValue: unknown;
  /** The proposed value, before any inline edit. */
  maValue: unknown;
  defaultChecked: boolean;
  /** Set when the row may not be included; the text says why. */
  locked: string | null;
}

export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Host and path only: scheme, `www.` and a trailing slash are not a difference. */
function foldUrl(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
}

function isLatLng(value: unknown): value is { lat: number; lng: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { lat?: unknown }).lat === 'number' &&
    typeof (value as { lng?: unknown }).lng === 'number'
  );
}

/** ~11 m. Tighter than any geocode is accurate to. */
const LAT_LNG_TOLERANCE = 1e-4;

export function valuesEqual(kind: FieldKind, a: unknown, b: unknown): boolean {
  if (kind === 'location') {
    return (
      isLatLng(a) &&
      isLatLng(b) &&
      Math.abs(a.lat - b.lat) < LAT_LNG_TOLERANCE &&
      Math.abs(a.lng - b.lng) < LAT_LNG_TOLERANCE
    );
  }
  if (typeof a === 'string' && typeof b === 'string') {
    if (kind === 'url') return foldUrl(a) === foldUrl(b);
    // Case counts. "Watch Co" and "watch co" are a conflict, not a match: an
    // equal row is ticked by default, and ticking it would replace WK's
    // capitalisation with MA's without anyone having chosen that.
    return a.trim() === b.trim();
  }
  if (kind === 'number') return Number(a) === Number(b);
  return a === b;
}

/**
 * The tick a row starts with. A conflict is the one state left UNTICKED, with
 * no per-field exception: WK's value is somebody's work, and overwriting it has
 * to be a decision, not a default.
 */
function defaultCheckedFor(state: RowState): boolean {
  return state === 'equal' || state === 'fill' || state === 'new';
}

/**
 * Build the grid's rows for one MA brand against a WK brand (Update), or
 * against nothing (Create, where every WK cell is empty).
 *
 * A row whose MA side is empty is dropped: there is nothing to propose, and no
 * action is possible on it either way.
 */
export function buildGridRows(
  ma: MaSource,
  wk: WatchBrand | null,
  specs: ProposalFieldSpec[] = PROPOSAL_FIELD_SPECS,
): GridRow[] {
  const rows: GridRow[] = [];
  for (const spec of specs) {
    const maValue = spec.maValue ? spec.maValue(ma) : getPath(ma, spec.maPath);
    if (isEmptyValue(maValue)) continue;

    let wkValue: unknown;
    if (wk) {
      wkValue = spec.wkValue ? spec.wkValue(wk) : spec.wkPath ? getPath(wk, spec.wkPath) : undefined;
    }
    const wkEmpty = isEmptyValue(wkValue) || (spec.wkFalseIsEmpty === true && wkValue === false);

    let state: RowState;
    if (spec.wkPath === null) {
      state = 'display';
    } else if (wkEmpty) {
      state = spec.isNewField ? 'new' : 'fill';
    } else {
      const equal =
        wk && spec.equals ? spec.equals(wk, maValue) : valuesEqual(spec.kind, wkValue, maValue);
      state = equal ? 'equal' : 'differ';
    }

    const locked = wk && state !== 'display' ? (spec.lockedReason?.(wk) ?? null) : null;

    rows.push({
      key: specKey(spec),
      spec,
      state,
      wkValue,
      maValue,
      locked,
      defaultChecked:
        state === 'display' || locked
          ? false
          : spec.defaultChecked
            ? spec.defaultChecked(state)
            : defaultCheckedFor(state),
    });
  }
  return rows;
}

/** A row the reviewer can tick: writable, and not locked. */
export function isTickable(row: GridRow): boolean {
  return row.state !== 'display' && !row.locked;
}

/**
 * The starting tick for every tickable row, keyed by row key. Locked and
 * display-only rows have no entry, so nothing restored from a saved draft can
 * tick them.
 */
export function defaultTicks(rows: GridRow[]): Record<string, boolean> {
  return Object.fromEntries(rows.filter(isTickable).map((row) => [row.key, row.defaultChecked]));
}

export function formatFieldValue(spec: ProposalFieldSpec, value: unknown): string {
  if (isEmptyValue(value)) return '';
  if (spec.formatter) return spec.formatter(value);
  if (isLatLng(value)) return `${value.lat}, ${value.lng}`;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function setPath(target: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split('.');
  let current = target;
  for (const part of parts.slice(0, -1)) {
    const next = current[part];
    if (typeof next !== 'object' || next === null) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

export interface ProposalSeedParts {
  values: Partial<WatchBrand>;
  externalRefs: BrandExternalRefs;
  fieldSources: BrandFieldSources;
}

/**
 * Turn the ticked rows into what BrandEditor's seed props carry: the values to
 * write, the MA reference, and one provenance stamp per ticked field.
 *
 * An inline edit changes the VALUE written; `rawValue` still records what MA
 * actually said. An edit that blanks the value drops the field altogether —
 * the import never writes an empty string over anything.
 */
export function buildSeedParts(params: {
  ma: MaSource;
  rows: GridRow[];
  ticks: Record<string, boolean>;
  /** Inline edits to the proposed value, keyed by row key. */
  edits?: Record<string, string>;
  now?: Date;
}): ProposalSeedParts {
  const { ma, rows, ticks, edits = {}, now = new Date() } = params;
  const values: Record<string, unknown> = {};
  const fieldSources: BrandFieldSources = {};

  for (const row of rows) {
    const { spec } = row;
    // A locked row is skipped even if `ticks` claims it: the lock is enforced
    // here, at the point the write is assembled, not only by a disabled input.
    if (spec.wkPath === null || row.locked || !ticks[row.key]) continue;
    const edited = edits[row.key];
    const value = spec.editable && edited !== undefined ? edited.trim() : row.maValue;
    if (isEmptyValue(value)) continue;
    setPath(values, spec.wkPath, value);
    fieldSources[spec.wkPath] = {
      source: MA_SOURCE,
      importedAt: now,
      rawValue: spec.maRawValue ? spec.maRawValue(ma) : row.maValue,
    };
  }

  return {
    values: values as Partial<WatchBrand>,
    externalRefs: { microbrandAtlas: { id: ma.id, slug: ma.slug, fetchedAt: now } },
    fieldSources,
  };
}

export function toCreateSeed(parts: ProposalSeedParts): CreateBrandSeed {
  return { ...parts, sourceLabel: MA_SOURCE_LABEL };
}
